import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSelectedAccount } from '../contexts/SelectedAccountContext';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api/apiClient';

function Sidebar({ activePage }) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { selectedAccountId, setSelectedAccountId } = useSelectedAccount();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Debug: Monitor selectedAccountId changes
  useEffect(() => {
    console.log('Sidebar: selectedAccountId changed to:', selectedAccountId);
  }, [selectedAccountId]);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(
    localStorage.getItem('language') || 'zh'
  );
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const profileMenuRef = useRef(null);

  // Fetch user accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoadingAccounts(true);
        const response = await apiClient.get('/user/social-accounts/account-settings');
        if (response.data && response.data.data) {
          setAccounts(response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch accounts:', error);
        setAccounts([]);
      } finally {
        setLoadingAccounts(false);
      }
    };

    fetchAccounts();
  }, []);

  // Handle click outside to close profile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu]);

  // Determine active page from location if not explicitly provided
  const getCurrentPage = () => {
    if (activePage) return activePage;
    const path = location.pathname;

    console.log('Sidebar getCurrentPage:', { activePage, pathname: path });

    if (path === '/posts-and-comments') return 'posts-comments';
    if (path === '/posts') return 'posts';
    if (path === '/dashboard-insights') return 'dashboard-insights';
    if (path === '/topic-tracking') return 'topic-tracking';
    if (path === '/my-automations') return 'automations';
    return '';
  };

  const currentPage = getCurrentPage();

  console.log('Sidebar currentPage:', currentPage, 'URL:', location.pathname);

  return (
    <div
      className="h-screen p-5 flex flex-col flex-shrink-0 transition-all duration-300 overflow-y-auto"
      style={{
        width: isCollapsed ? '80px' : '280px',
        background: theme.bgSecondary,
        borderRight: `1px solid ${theme.borderColor}`
      }}
    >
      {/* Header: Logo + Collapse Button */}
      <div className="flex items-center justify-between mb-6">
        {!isCollapsed && (
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate('/home')}
          >
            <img
              src="/images/viralarc-logo.png"
              alt="ViralArc.ai"
              className="w-8 h-8"
              onError={(e) => {
                // Fallback to text logo if image not found
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
            <div
              className="w-8 h-8 rounded-lg items-center justify-center font-bold text-sm"
              style={{
                background: theme.buttonPrimary,
                color: theme.buttonPrimaryText,
                display: 'none'
              }}
            >
              V
            </div>
            <span className="font-bold text-lg" style={{ color: theme.textPrimary }}>
              ViralArc.ai
            </span>
          </div>
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={`p-2 rounded-lg border-none cursor-pointer transition-all hover:opacity-80 ${isCollapsed ? 'mx-auto' : ''}`}
          style={{ background: theme.bgHover, color: theme.textPrimary }}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon
            icon={isCollapsed ? "mdi:chevron-right" : "mdi:chevron-left"}
            className="text-xl"
          />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1">
        <NavItem
          icon="mdi:robot-outline"
          label="My Automations"
          active={currentPage === 'automations'}
          theme={theme}
          onClick={() => navigate('/my-automations')}
          isCollapsed={isCollapsed}
        />

        <NavItem
          icon="mdi:message-text-outline"
          label="Posts & Comments"
          active={currentPage === 'posts-comments'}
          theme={theme}
          onClick={() => navigate('/posts-and-comments')}
          isCollapsed={isCollapsed}
        />

        <NavItem
          icon="mdi:text-box-outline"
          label="Posting center"
          active={currentPage === 'posts'}
          theme={theme}
          onClick={() => navigate('/posts')}
          isCollapsed={isCollapsed}
        />

        <NavItem
          icon="mdi:chart-line"
          label="Insights"
          active={currentPage === 'dashboard-insights'}
          theme={theme}
          onClick={() => navigate('/dashboard-insights')}
          isCollapsed={isCollapsed}
        />

        <NavItem
          icon="mdi:target"
          label="Topic Tracking"
          active={currentPage === 'topic-tracking'}
          theme={theme}
          onClick={() => navigate('/topic-tracking')}
          isCollapsed={isCollapsed}
        />

        {/* Divider */}
        {!isCollapsed && (
          <div className="h-px my-3" style={{ background: theme.borderColor }} />
        )}

        {/* Connected Accounts Section */}
        {!isCollapsed && (
          <div className="mb-3">
            <div className="px-3 mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase" style={{ color: theme.textTertiary }}>
                Accounts
              </span>
            </div>

            {loadingAccounts ? (
              <div className="px-3 py-2 text-xs" style={{ color: theme.textSecondary }}>
                <Icon icon="line-md:loading-loop" className="inline mr-2" />
                Loading...
              </div>
            ) : accounts.length > 0 ? (
              <div className="space-y-1">
                {accounts.map((account) => (
                  <AccountItem
                    key={account.id}
                    account={account}
                    theme={theme}
                    currentPage={currentPage}
                    selectedAccountId={selectedAccountId}
                    onSelect={(accountId) => {
                      console.log('Sidebar onSelect called:', {
                        accountId,
                        currentPage
                      });
                      console.log('Setting selected account to:', accountId);
                      setSelectedAccountId(accountId);
                    }}
                    onDelete={(accountId) => {
                      setAccounts(accounts.filter(a => a.id !== accountId));
                    }}
                  />
                ))}
              </div>
            ) : (
              <div className="px-3 py-2 text-xs" style={{ color: theme.textSecondary }}>
                No accounts connected
              </div>
            )}
          </div>
        )}

        {/* Add Account Button */}
        <div
          onClick={() => navigate('/onboarding/step1')}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all mb-1 ${isCollapsed ? 'justify-center' : ''}`}
          style={{
            background: 'transparent',
            color: theme.textSecondary,
            border: `1px dashed ${theme.borderColor}`
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = theme.bgHover;
            e.currentTarget.style.borderColor = theme.buttonPrimary;
            e.currentTarget.style.color = theme.buttonPrimary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = theme.borderColor;
            e.currentTarget.style.color = theme.textSecondary;
          }}
          title={isCollapsed ? 'Add Account' : ''}
        >
          <Icon icon="mdi:plus-circle-outline" className={isCollapsed ? "text-xl" : "text-lg"} />
          {!isCollapsed && (
            <span className="text-sm font-medium">Add Account</span>
          )}
        </div>
      </nav>

      {/* User Profile - Moved to Bottom */}
      <div ref={profileMenuRef} className="relative mb-3">
        {/* Profile Dropdown Menu - Opens Upward */}
        {showProfileMenu && !isCollapsed && (
          <div
            className="absolute bottom-full left-0 right-0 mb-2 rounded-xl shadow-lg z-[1000] overflow-hidden"
            style={{ background: theme.bgSecondary, border: `1px solid ${theme.borderColor}` }}
          >
            {/* User Info Section */}
            <div className="p-4" style={{ borderBottom: `1px solid ${theme.borderColor}` }}>
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm uppercase flex-shrink-0"
                  style={{ background: theme.buttonPrimary, color: theme.buttonPrimaryText }}
                >
                  {user?.email?.charAt(0) || 'U'}
                </div>
                <div className="font-medium text-sm truncate" style={{ color: theme.textPrimary }}>
                  {user?.email || 'User'}
                </div>
              </div>
            </div>

            {/* Plan Section */}
            <div
              className="px-4 py-3 flex items-center justify-between"
              style={{ borderBottom: `1px solid ${theme.borderColor}` }}
            >
              <span className="text-sm font-semibold" style={{ color: theme.textPrimary }}>
                {t('subscription.free')}
              </span>
              <button
                onClick={() => {
                  setShowProfileMenu(false);
                  navigate('/pricing');
                }}
                className="px-3 py-1 border-none rounded-md text-xs font-medium cursor-pointer"
                style={{ background: theme.buttonPrimary, color: theme.buttonPrimaryText }}
              >
                {t('subscription.upgrade')}
              </button>
            </div>

            {/* Credits */}
            <ProfileMenuItem
              icon="mdi:lightning-bolt"
              label={t('menu.credits')}
              value="692"
              theme={theme}
              onClick={() => {
                setShowProfileMenu(false);
                navigate('/credits');
              }}
              showArrow
            />

            <div className="h-px my-1" style={{ background: theme.borderColor }} />

            {/* Menu Items */}
            <ProfileMenuItem
              icon="mdi:account-outline"
              label={t('menu.account')}
              theme={theme}
              onClick={() => {
                setShowProfileMenu(false);
                navigate('/account');
              }}
            />
            <ProfileMenuItem
              icon="mdi:cog-outline"
              label={t('menu.settings')}
              theme={theme}
              onClick={() => {
                setShowProfileMenu(false);
                navigate('/settings');
              }}
            />

            <div className="h-px my-1" style={{ background: theme.borderColor }} />

            <ProfileMenuItem
              icon="mdi:home-outline"
              label={t('menu.homepage')}
              theme={theme}
              onClick={() => {
                setShowProfileMenu(false);
                navigate('/');
              }}
              showExternalIcon
            />
            <ProfileMenuItem
              icon="mdi:help-circle-outline"
              label={t('menu.getHelp')}
              theme={theme}
              onClick={() => {
                setShowProfileMenu(false);
                window.open('https://help.viralarc.ai', '_blank');
              }}
              showExternalIcon
            />

            {/* Language Selector */}
            <LanguageMenuItem
              theme={theme}
              currentLanguage={currentLanguage}
              onLanguageChange={(lang) => {
                setCurrentLanguage(lang);
                localStorage.setItem('language', lang);
                i18n.changeLanguage(lang);
              }}
            />

            <div className="h-px my-1" style={{ background: theme.borderColor }} />

            <ProfileMenuItem
              icon="mdi:logout"
              label={t('menu.signOut')}
              theme={theme}
              onClick={() => {
                setShowProfileMenu(false);
                localStorage.removeItem('token');
                navigate('/login');
              }}
              danger
            />
          </div>
        )}

        {/* Profile Button */}
        <div
          onClick={() => !isCollapsed && setShowProfileMenu(!showProfileMenu)}
          className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${isCollapsed ? 'justify-center' : 'cursor-pointer hover:opacity-90'}`}
          style={{ background: showProfileMenu ? theme.bgHover : 'transparent' }}
          onMouseEnter={(e) => !isCollapsed && (e.currentTarget.style.background = theme.bgHover)}
          onMouseLeave={(e) => {
            if (!showProfileMenu && !isCollapsed) e.currentTarget.style.background = 'transparent';
          }}
          title={isCollapsed ? user?.email || 'User' : ''}
        >
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm uppercase flex-shrink-0"
            style={{ background: theme.buttonPrimary, color: theme.buttonPrimaryText }}
          >
            {user?.email?.charAt(0) || 'U'}
          </div>
          {!isCollapsed && (
            <>
              <span className="font-medium text-sm truncate" style={{ color: theme.textPrimary }}>{user?.email || 'User'}</span>
              <Icon
                icon="mdi:chevron-up"
                className={`ml-auto text-xl transition-transform duration-200 ${showProfileMenu ? 'rotate-180' : ''}`}
                style={{ color: theme.textSecondary }}
              />
            </>
          )}
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="mt-auto">
        {!isCollapsed ? (
          <>
            <button
              onClick={() => navigate('/pricing')}
              className="w-full px-4 py-2.5 border-none rounded-lg text-sm font-medium cursor-pointer flex items-center gap-2 mb-3 transition-opacity hover:opacity-80"
              style={{ background: theme.buttonSecondary, color: theme.textPrimary }}
            >
              <Icon icon="mdi:crown" className="text-lg" />
              Upgrade Your Plan
            </button>

            <div className="flex items-center gap-3 text-[13px]" style={{ color: theme.textSecondary }}>
              <span>Follow us</span>
              <Icon icon="mdi:twitter" className="cursor-pointer text-lg" />
              <Icon icon="mdi:instagram" className="cursor-pointer text-lg" />
              <Icon icon="mdi:github" className="cursor-pointer text-lg" />
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <Icon
              icon="mdi:crown"
              className="cursor-pointer text-2xl"
              style={{ color: theme.textSecondary }}
              onClick={() => navigate('/pricing')}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function NavItem({ icon, label, active, theme, onClick, isCollapsed }) {
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all mb-1 ${isCollapsed ? 'justify-center' : ''}`}
      style={{
        background: active ? theme.bgHover : 'transparent',
        color: active ? theme.textPrimary : theme.textSecondary
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = theme.bgHover;
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = 'transparent';
      }}
      title={isCollapsed ? label : ''}
    >
      <Icon icon={icon} className={isCollapsed ? "text-xl" : "text-lg"} />
      {!isCollapsed && (
        <span className={`text-sm ${active ? 'font-medium' : 'font-normal'}`}>{label}</span>
      )}
    </div>
  );
}

function ProfileMenuItem({ icon, label, value, theme, onClick, showArrow, showExternalIcon, danger }) {
  return (
    <div
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-all bg-transparent"
      style={{ color: danger ? '#ef4444' : theme.textPrimary }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = danger ? 'rgba(239, 68, 68, 0.1)' : theme.bgHover;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <Icon
        icon={icon}
        className="text-lg"
        style={{ color: danger ? '#ef4444' : theme.textSecondary }}
      />
      <span className="text-sm font-normal flex-1">{label}</span>
      {value && (
        <span className="text-sm font-medium" style={{ color: theme.textPrimary }}>
          {value}
        </span>
      )}
      {showArrow && (
        <Icon icon="mdi:chevron-right" className="text-lg" style={{ color: theme.textTertiary }} />
      )}
      {showExternalIcon && (
        <Icon icon="mdi:open-in-new" className="text-base" style={{ color: theme.textTertiary }} />
      )}
    </div>
  );
}

function LanguageMenuItem({ theme, currentLanguage, onLanguageChange }) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'zh', name: '中文'}
  ];

  const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[1];

  return (
    <div>
      {/* Main Language Button */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-all bg-transparent"
        style={{ color: theme.textPrimary }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = theme.bgHover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
        }}
      >
        <Icon icon="mdi:translate" className="text-lg" style={{ color: theme.textSecondary }} />
        <span className="text-sm font-normal flex-1">{t('menu.language')}</span>
        <span className="text-sm opacity-70">
          {currentLang.flag} {currentLang.code.toUpperCase()}
        </span>
        <Icon
          icon={isExpanded ? "mdi:chevron-up" : "mdi:chevron-down"}
          className="text-base opacity-50"
          style={{ color: theme.textTertiary }}
        />
      </div>

      {/* Language Options Dropdown */}
      {isExpanded && (
        <div
          className="pl-[46px] rounded mx-2"
          style={{ background: theme.bgHover }}
        >
          {languages.map((lang) => (
            <div
              key={lang.code}
              onClick={() => {
                onLanguageChange(lang.code);
                setIsExpanded(false);
              }}
              className="flex items-center gap-2 px-3 py-2.5 cursor-pointer rounded transition-all text-[13px]"
              style={{
                background: currentLanguage === lang.code ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                color: currentLanguage === lang.code ? '#3b82f6' : theme.textSecondary
              }}
              onMouseEnter={(e) => {
                if (currentLanguage !== lang.code) {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentLanguage !== lang.code) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <span>{lang.flag}</span>
              <span>{lang.name}</span>
              {currentLanguage === lang.code && (
                <Icon icon="mdi:check" className="text-base ml-auto" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AccountItem({ account, theme, currentPage, selectedAccountId, onSelect, onDelete }) {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const menuRef = useRef(null);

  // 所有頁面都可以選擇帳號
  const isSelected = selectedAccountId === account.id;
  const isSelectable = account.platform === 'threads' && account.status === 'active';

  // Debug logging
  // console.log('AccountItem Debug:', {
  //   accountId: account.id,
  //   accountName: account.account_name || account.accountName,
  //   platform: account.platform,
  //   status: account.status,
  //   selectedAccountId,
  //   isSelected,
  //   isSelectable
  // });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const handleEdit = () => {
    setShowMenu(false);
    // 導航到 step1 並帶上帳號 ID
    navigate(`/onboarding/step1?edit=${account.id}`);
  };

  const handleDelete = async () => {
    if (!window.confirm(`確定要刪除帳號 @${account.account_name || account.account || account.username} 嗎？`)) {
      return;
    }

    setIsDeleting(true);
    try {
      await apiClient.delete(`/user/social-accounts/account-settings/id/${account.id}`);
      onDelete(account.id);
    } catch (error) {
      console.error('Failed to delete account:', error);
      alert('刪除失敗，請稍後再試');
    } finally {
      setIsDeleting(false);
      setShowMenu(false);
    }
  };

  const platformIcons = {
    threads: 'mdi:at',
    instagram: 'mdi:instagram'
  };

  return (
    <div
      className={`relative flex items-center gap-2 px-3 py-2.5 rounded-xl transition-all duration-200 ${
        isSelectable ? 'cursor-pointer' : ''
      }`}
      style={{
        background: isSelected ? 'rgba(37, 99, 235, 0.1)' : 'transparent',
        position: 'relative'
      }}
      onClick={() => {
        console.log('AccountItem clicked:', {
          accountId: account.id,
          isSelectable,
          hasOnSelect: !!onSelect,
          currentPage
        });
        if (isSelectable && onSelect) {
          console.log('Calling onSelect with:', account.id);
          onSelect(account.id);
        } else {
          console.log('Not selectable or no onSelect function');
        }
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.background = theme.bgHover;
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.background = 'transparent';
        }
      }}
    >
      {/* Left blue indicator bar when selected */}
      {isSelected && (
        <>
          <div
            className="absolute -left-5 top-1/2 -translate-y-1/2 w-1 h-8 bg-blue-600 rounded-r-full"
          />
          <div
            className="absolute -left-5 top-0 bottom-0 w-6 pointer-events-none"
            style={{
              background: 'linear-gradient(to right, rgba(37, 99, 235, 0.15) 0%, transparent 100%)',
              borderTopRightRadius: '16px',
              borderBottomRightRadius: '16px'
            }}
          />
        </>
      )}
      {/* Platform Icon */}
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 relative z-10"
        style={{
          background: isSelected ? theme.buttonPrimary : theme.buttonPrimary,
          color: 'white'
        }}
      >
        <Icon
          icon={platformIcons[account.platform] || 'mdi:account-circle'}
          className="text-base"
        />
      </div>

      {/* Account Name */}
      <div className="flex-1 min-w-0 relative z-10">
        <div
          className={`text-sm font-semibold truncate ${isSelected ? 'text-blue-600' : ''}`}
          style={{ color: isSelected ? undefined : theme.textPrimary }}
        >
          @{account.account_name || account.account || account.username || 'Unknown'}
        </div>
        {account.platform && (
          <div
            className="text-xs capitalize"
            style={{ color: theme.textTertiary }}
          >
            {account.platform}
          </div>
        )}
      </div>

      {/* Three Dots Menu */}
      <div className="relative" ref={menuRef}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowMenu(!showMenu);
          }}
          className="p-1 rounded hover:bg-gray-200/50 transition-colors"
          style={{ color: theme.textSecondary }}
          disabled={isDeleting}
        >
          <Icon icon="mdi:dots-vertical" className="text-base" />
        </button>

        {/* Dropdown Menu */}
        {showMenu && (
          <div
            className="absolute right-0 bottom-full mb-1 rounded-lg shadow-lg z-50 overflow-hidden min-w-[120px]"
            style={{ background: theme.bgSecondary, border: `1px solid ${theme.borderColor}` }}
          >
            <button
              onClick={handleEdit}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs cursor-pointer transition-all border-none text-left"
              style={{
                background: 'transparent',
                color: theme.textPrimary
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = theme.bgHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <Icon icon="mdi:pencil-outline" className="text-sm" />
              Edit
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs cursor-pointer transition-all border-none text-left"
              style={{
                background: 'transparent',
                color: '#ef4444'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <Icon icon={isDeleting ? "line-md:loading-loop" : "mdi:delete-outline"} className="text-sm" />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Sidebar;
